import { useMemo, useState, useEffect, ComponentType } from 'react';
import { MenuProps, createFilter, components as parts } from 'react-select';

import { Select } from 'components/select';
import { formatTimeData } from 'utils/date';
import { TimeDropdownIndicator } from 'components/select/components';
import {
  parseTimeInput,
  parseRangeInput,
  typedTimeProblem,
} from 'utils/show-hours-parser';

import { getOptions } from './helpers';
import { TimePickerProps } from './types';

const defaultFilter = createFilter({ ignoreAccents: false });

/**
 * A typeable box's menu: as wide as the box for the list, wider for a
 * one-line message ("Use 5-minute times, e.g. 10:50"), never past the
 * screen; a box on the right opens it from its right edge.
 */
const wideMenu = (align: 'left' | 'right') => {
  const WideMenu = (props: MenuProps<any>) => (
    <parts.Menu
      {...props}
      innerProps={{
        ...props.innerProps,
        style: {
          minWidth: '100%',
          width: 'max-content',
          maxWidth: 'calc(100vw - 32px)',
          ...(align === 'right' ? { right: 0, left: 'auto' } : {}),
        },
      }}
    />
  );
  return WideMenu;
};
const WideMenuLeft = wideMenu('left');
const WideMenuRight = wideMenu('right');

export const TimePicker = ({
  after,
  value,
  typeable,
  onChange,
  minValue,
  timeRole,
  step = 15,
  components,
  onRangeInput,
  onTypedProblem,
  menuAlign = 'left',
  isSearchable = true,
  setDefaultSelectedValue,
  ...props
}: TimePickerProps) => {
  const options = useMemo(() => getOptions(step, minValue), [step, minValue]);
  const [inputValue, setInputValue] = useState('');

  const selectedValue = useMemo(() => {
    if (!value && value !== 0) {
      return undefined;
    }
    // A typeable box (show hours) shows a saved time exactly as it is,
    // even off its list, never the nearest one (owner, 23 Oct).
    const exact = options.find((option) => Number(option.value) === value);
    if (exact) {
      return exact;
    }
    if (typeable) {
      return { value: String(value), label: formatTimeData(value) };
    }
    const sorted = [...options];
    sorted.sort(({ value: a }, { value: b }) => {
      const aDiff = Math.abs(Number(a) - value);
      const bDiff = Math.abs(Number(b) - value);
      if (aDiff === bDiff) {
        return 0;
      }
      return aDiff > bDiff ? 1 : -1;
    });
    return sorted[0];
  }, [options, value]);

  useEffect(() => {
    const checkedValue = Number(selectedValue?.value);

    if (
      selectedValue &&
      setDefaultSelectedValue &&
      typeof value === 'number' &&
      checkedValue !== value
    ) {
      onChange(checkedValue);
    }
  }, [selectedValue?.value]);

  // A typed time is taken exactly as typed, or refused: never moved to
  // another time (owner, 23 Oct: "10:50" was saved as 10:45).
  const typed = useMemo(() => {
    if (!typeable || !inputValue.trim() || !options.length) {
      return undefined;
    }
    const ms = parseTimeInput(inputValue, timeRole, after);
    return ms === undefined
      ? undefined
      : options.find((option) => Number(option.value) === ms);
  }, [typeable, inputValue, timeRole, after, options]);

  const typedRange =
    typeable && onRangeInput && !typed
      ? parseRangeInput(inputValue)
      : undefined;

  // Why what is typed is refused ("10:52": Use 5-minute times, e.g. 10:50).
  const problem = useMemo(() => {
    if (!typeable || typed || typedRange || !inputValue.trim()) {
      return undefined;
    }
    const why = typedTimeProblem(inputValue, timeRole, after);
    if (why) {
      return why;
    }
    return parseTimeInput(inputValue, timeRole, after) === undefined
      ? undefined
      : `Use ${step}-minute times`;
  }, [typeable, typed, typedRange, inputValue, timeRole, after, step]);

  const typingProps = typeable
    ? {
        inputValue,
        noOptionsMessage: () =>
          typedRange
            ? 'Press Enter to fill From and To'
            : (problem ?? 'Not a time'),
        filterOption: (
          option: { label: string; value: string; data: unknown },
          text: string
        ) =>
          typed
            ? option.value === typed.value
            : !typedRange && !problem && defaultFilter(option as any, text),
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' && problem) {
            // Enter on a refused time ("10:52"): nothing is picked and the
            // form is not sent; the field says why.
            event.preventDefault();
            onTypedProblem?.(problem);
            return;
          }
          if (event.key === 'Enter' && typedRange) {
            event.preventDefault();
            onRangeInput?.(typedRange);
            setInputValue('');
          }
        },
        onInputChange: (text: string, meta: { action: string }) => {
          if (meta.action === 'input-change') {
            setInputValue(text);
            return;
          }
          if (meta.action === 'input-blur') {
            if (typed && Number(typed.value) !== value) {
              onChange(Number(typed.value));
            } else if (typedRange) {
              onRangeInput?.(typedRange);
            }
            // Leaving the box with a refused time: the value stays as it
            // was, and the field says why (until the next good time).
            if (inputValue.trim()) {
              onTypedProblem?.(problem);
            }
          }
          setInputValue('');
        },
      }
    : {};

  return (
    <Select
      defaultControl
      options={options}
      isSearchable={isSearchable}
      // Always controlled: null (not undefined) when there is no time, so
      // react-select never falls back to the option it last picked itself
      // (Dev 23 Oct: a Closed row kept showing a hand-picked 9:00 am).
      value={selectedValue ?? null}
      onChange={(newValue) => {
        // @ts-ignore
        const result = newValue?.value;
        onTypedProblem?.(undefined);
        onChange(result ? Number(result) : undefined);
      }}
      components={{
        DropdownIndicator: TimeDropdownIndicator as ComponentType<any>,
        ...(typeable && {
          Menu: menuAlign === 'right' ? WideMenuRight : WideMenuLeft,
        }),
        ...components,
      }}
      {...typingProps}
      {...props}
    />
  );
};

export type { TimePickerProps };
